import { memo, useCallback, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { getImpersonation } from '@common/auth'
import { queryClient } from '@common/queryClient'
import { clearSession } from '@common/session'
import { logout } from '@api'

const exitButtonSx = {
  color: '#fff',
  borderColor: 'rgba(255,255,255,0.7)',
  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
}

// Impersonation.TokenSource from the redeem payload; absent on sessions started
// before the backend surfaced it, in which case no label is shown.
const TOKEN_SOURCE_LABELS: Record<string, string> = {
  Active: 'active token',
  Generated: 'generated token',
}

const bannerSx = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  bgcolor: '#E04F4F',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  px: 3,
  py: 0.75,
  boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
}

const ImpersonationBanner = memo(() => {
  // Re-read on each render so it reflects fresh localStorage state after login.
  const [impersonation] = useState(() => getImpersonation())

  // Exit clears the impersonated session entirely and returns to login. We never
  // restore a stashed admin token (see enterImpersonation) — the admin re-auths.
  const handleExit = useCallback(async () => {
    // Revoke the impersonation token server-side first: without this the
    // minted session stayed Active until its TTL even though nobody could
    // use it from this browser anymore. Best-effort — exit must proceed
    // even if the call fails. Safe under the dedicated-copy design: Logout
    // archives only the impersonation token, never the target's session.
    try {
      await logout()
    } catch (_) {
      // best-effort
    }
    clearSession(queryClient)
    window.location.href = '/login'
  }, [])

  if (!impersonation) return null

  const tokenSourceLabel = TOKEN_SOURCE_LABELS[impersonation.tokenSource] || ''

  return (
    <Box sx={bannerSx}>
      <Typography variant="body2" fontWeight={600}>
        Impersonating {impersonation.targetName}
        {tokenSourceLabel && ` · ${tokenSourceLabel}`}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={handleExit}
        sx={exitButtonSx}
      >
        Exit
      </Button>
    </Box>
  )
})

export default ImpersonationBanner
